import type { Script } from '../script/schema';

export interface SceneSelectCallbacks {
  onJump: (sceneId: string) => void;
  onClose: () => void;
}

/**
 * Scene IDs that should be hidden from the chapter-select grid.
 * These are auto-advancing jargon/tutorial slides — not navigable chapters.
 */
const SCENE_HIDDEN = new Set([
  'grounding-scene',
  'grounding-scene-2',
  'grounding-scene-3',
  'grounding-scene-4',
  'grounding-scene-5',
  // CG interstitials that aren't meaningful chapter jump points
  'cg-cg-stranger-no-threads',
  'cg-cg-archive-discovery',
  'cg-cg-loom-confession',
  'cg-cg-loom-beneath',
  'cg-cg-fracture',
  'cg-cg-city-stitched',
  'cg-cg-witness-writes',
  'cg-cg-kitchen-held',
  // Dead draft superseded by s34a-scene-threshold-chamber — no incoming references
  's34-scene-threshold',
]);

/**
 * Curated chapter titles + chronological order keyed by scene beat ID.
 * Order is the narrative chapter order (prologue → act 1 → act 2 → act 3 → endings),
 * NOT the JSON authoring order (which interleaves later patch beats).
 *
 * Anything not in this map falls back to `labelFromId` and sorts to the end.
 * Entries in SCENE_HIDDEN are excluded from the grid entirely.
 */
const SCENE_INDEX: Record<string, { title: string; order: number; act: string }> = {
  // Prologue
  'prologue-bg':                    { title: 'Prologue — The City of Threads',    order: 100, act: 'Prologue' },
  'grounding-thread-pull':          { title: 'The Thread Pull',                   order: 160, act: 'Prologue' },

  // Act 1
  's1-scene-atelier':                 { title: '1. Morning at the Atelier',              order: 200, act: 'Act I' },
  's2-scene-reading-and-stranger':    { title: '2. The Public Reading',                  order: 210, act: 'Act I' },
  's3-scene-courtyard':               { title: '3. The Stranger in the Courtyard',       order: 220, act: 'Act I' },
  's4-scene-sidebyside':              { title: '4. Side by Side',                        order: 230, act: 'Act I' },
  's5-scene-archive':                 { title: '5. The Archive',                         order: 240, act: 'Act I' },
  's6-scene-looms-study':             { title: "6. Loom's Warning",                      order: 250, act: 'Act I' },
  's7-scene-market':                  { title: '7. Market Day',                          order: 260, act: 'Act I' },
  's8-scene-eastern-wall':            { title: '8. The Eastern Wall',                    order: 270, act: 'Act I' },
  's10-scene-wicks-library':          { title: "10. Wick's Library",                     order: 280, act: 'Act I' },
  's11-scene-return':                 { title: '11. The Return',                         order: 290, act: 'Act I' },

  // Act 2
  's12-scene-herself':                { title: '12. Reading Herself',                    order: 300, act: 'Act II' },
  's13-scene-vault':                  { title: '13. Rue in the Vault',                   order: 310, act: 'Act II' },
  's14-scene-atelier-after':          { title: '14. The Atelier After',                  order: 320, act: 'Act II' },
  's15-scene-truths':                 { title: '15. Trading Truths',                     order: 330, act: 'Act II' },
  's16-scene-flower':                 { title: '16. The Pressed Flower',                 order: 340, act: 'Act II' },
  's17-scene-wick-speaks':            { title: '17. Wick Speaks',                        order: 350, act: 'Act II' },
  's18-scene-marens-price':           { title: "18. Maren's Price",                      order: 360, act: 'Act II' },
  's19-scene-looms-confession':       { title: "19. Loom's Confession",                  order: 370, act: 'Act II' },
  's20-scene-surge-city':             { title: '20. The Surge — City',                   order: 380, act: 'Act II' },
  's21-scene-surge-sable':            { title: '21. The Surge — Sable',                  order: 390, act: 'Act II' },
  's22-scene-loom-beneath':           { title: '22. The Loom Beneath Speaks',            order: 400, act: 'Act II' },
  's23-scene-fractures':              { title: '23. The City Fractures',                 order: 410, act: 'Act II' },
  's23dot5-scene':                    { title: '23.5. The City After the Vote',           order: 420, act: 'Act II' },
  's24-scene-gambit':                 { title: "24. Rue's Gambit",                       order: 430, act: 'Act II' },

  // Act 3
  's25a-publish':                     { title: '25. The Publish',                        order: 500, act: 'Act III' },
  's25b-three-day':                   { title: '25b. The Three-Day Vote',                order: 510, act: 'Act III' },
  's25c-descent':                     { title: '25c. The Descent',                       order: 520, act: 'Act III' },
  's26-scene-loom-atelier':           { title: '26. The Cost',                           order: 530, act: 'Act III' },
  's27-scene-cycle-visible':          { title: '27. The Cycle Visible',                  order: 540, act: 'Act III' },
  's28-scene-loom-beneath-again':     { title: '28. The Loom Beneath, Again',            order: 550, act: 'Act III' },
  's29-scene-quiet-day':              { title: '29. The Quiet Day',                      order: 560, act: 'Act III' },
  's29dot5-scene':                    { title: '29.5. What the City Wants',              order: 570, act: 'Act III' },
  's30-scene-hall-reconvenes':        { title: '30. The Hall Reconvenes',                order: 580, act: 'Act III' },
  's31-scene-indigo-speaks':          { title: '31. The Indigo Speaks',                  order: 590, act: 'Act III' },
  's32-scene-witness-writes':         { title: '32. The Witness Writes',                 order: 600, act: 'Act III' },
  's33-scene-sables-choice':          { title: "33. Sable's Choice",                     order: 610, act: 'Act III' },
  's34-scene-threshold':              { title: '34. The Threshold',                      order: 620, act: 'Act III' },
  's34a-scene-threshold-chamber':     { title: '34a. The Threshold Chamber',             order: 630, act: 'Act III' },

  // Endings
  's35a-held':                        { title: '35a. Ending — Held',                     order: 700, act: 'Endings' },
  's35b-scene-many-hands':            { title: '35b. Ending — Many Hands',               order: 710, act: 'Endings' },
  's35c-scene-closed':                { title: '35c. Ending — We Were Always Listening', order: 720, act: 'Endings' },
  's35x-scene':                       { title: '35x. After the Choice',                  order: 730, act: 'Endings' },
  's34b-end-repaired':                { title: '34b. Ending — Repaired',                 order: 740, act: 'Endings' },
  's34c-end-closed':                  { title: '34c. Ending — Closed',                   order: 750, act: 'Endings' },
};

/**
 * Human-readable label for a scene beat ID.
 * Prefers the curated `SCENE_INDEX` title. Falls back to a derived title
 * by stripping known prefixes, converting separators to spaces, and title-casing.
 */
export function labelFromId(id: string): string {
  const indexed = SCENE_INDEX[id];
  if (indexed) return indexed.title;
  return id
    .replace(/^(s\d+[.-]?|cg-|grounding-|prologue-)/i, '')
    .replace(/[-_.]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || id;
}

/**
 * Sort priority for a scene beat ID.
 * Curated entries use their `order`; uncurated entries sort to the end
 * but preserve their original script-order via the index fallback.
 */
function sceneOrder(id: string, fallbackIndex: number): number {
  const indexed = SCENE_INDEX[id];
  return indexed ? indexed.order : 10_000 + fallbackIndex;
}

export interface SceneEntry {
  id: string;
  label: string;
  act: string;
  bg: string | undefined;
  visited: boolean;
}

/**
 * Build the ordered list of scene entries from the script.
 * Hidden scenes (grounding slides, CG interstitials) are excluded.
 * Entries are sorted by curated narrative order (see `SCENE_INDEX`).
 */
export function buildSceneEntries(
  script: Script,
  visitedIds: string[],
): SceneEntry[] {
  const visited = new Set(visitedIds);
  const sceneBeats = script.beats
    .map((b, idx) => ({ beat: b, idx }))
    .filter(({ beat }) => beat.kind === 'scene' && !SCENE_HIDDEN.has(beat.id));

  return sceneBeats
    .map(({ beat: b, idx }) => {
      const beat = b as { id: string; kind: string; label?: string; bg?: string };
      const indexed = SCENE_INDEX[beat.id];
      return {
        id: beat.id,
        label: beat.label ?? labelFromId(beat.id),
        act: indexed?.act ?? 'Other',
        bg: beat.bg,
        visited: visited.has(beat.id),
        _order: sceneOrder(beat.id, idx),
      };
    })
    .sort((a, b) => a._order - b._order)
    .map(({ _order: _, ...entry }) => entry);
}

/**
 * Renders the Scene Select modal overlay.
 * Visited scenes are clickable with a bg thumbnail; unvisited are shown locked.
 */
export function renderSceneSelectModal(
  root: HTMLElement,
  script: Script,
  visitedIds: string[],
  callbacks: SceneSelectCallbacks,
): void {
  const existing = root.querySelector('#scene-select-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'scene-select-modal';
  overlay.className = 'ss-overlay';

  const panel = document.createElement('div');
  panel.className = 'ss-panel';

  // Header
  const header = document.createElement('div');
  header.className = 'ss-header';

  const title = document.createElement('h2');
  title.textContent = 'Scenes';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'ss-close';
  closeBtn.textContent = '✕';
  closeBtn.setAttribute('aria-label', 'Close scene select');
  closeBtn.addEventListener('click', () => close());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Scene list grouped by act
  const entries = buildSceneEntries(script, visitedIds);

  // Group entries by act, preserving order of first appearance
  const actOrder: string[] = [];
  const byAct: Record<string, SceneEntry[]> = {};
  for (const entry of entries) {
    if (!byAct[entry.act]) {
      actOrder.push(entry.act);
      byAct[entry.act] = [];
    }
    byAct[entry.act].push(entry);
  }

  const body = document.createElement('div');
  body.className = 'ss-body';

  for (const act of actOrder) {
    // Act section header
    const actHeader = document.createElement('div');
    actHeader.className = 'ss-act-header';
    actHeader.textContent = act;
    body.appendChild(actHeader);

    // Grid for this act
    const grid = document.createElement('div');
    grid.className = 'ss-grid';

    for (const entry of byAct[act]) {
      const card = document.createElement('button');
      card.className = `ss-card${entry.visited ? '' : ' ss-card--locked'}`;
      card.disabled = !entry.visited;
      card.setAttribute(
        'aria-label',
        entry.visited ? `Jump to: ${entry.label}` : `${entry.label} (not yet reached)`,
      );

      // Thumbnail
      const thumb = document.createElement('div');
      thumb.className = 'ss-thumb';
      if (entry.bg && entry.visited) {
        const bgPath = entry.bg.startsWith('assets/')
          ? `./${entry.bg}`
          : `./assets/bg/${entry.bg}`;
        thumb.style.backgroundImage = `url('${bgPath}')`;
        thumb.style.backgroundSize = 'cover';
        thumb.style.backgroundPosition = 'center';
      }
      if (!entry.visited) {
        const lock = document.createElement('span');
        lock.className = 'ss-lock';
        lock.textContent = '🔒';
        lock.setAttribute('aria-hidden', 'true');
        thumb.appendChild(lock);
      }
      card.appendChild(thumb);

      // Label — always visible
      const lbl = document.createElement('div');
      lbl.className = 'ss-label';
      lbl.textContent = entry.label;
      card.appendChild(lbl);

      if (entry.visited) {
        card.addEventListener('click', () => {
          close();
          callbacks.onJump(entry.id);
        });
      }

      grid.appendChild(card);
    }

    body.appendChild(grid);
  }

  panel.appendChild(body);
  overlay.appendChild(panel);

  // Close on backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  // Esc to close
  const escHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', escHandler);

  root.appendChild(overlay);

  function close(): void {
    document.removeEventListener('keydown', escHandler);
    overlay.remove();
    callbacks.onClose();
  }
}
